import { FileContent, FileTypes, UploadDocumentType } from '../models/files.model';

export class FilesUtils {
  static readonly MEGABYTE = 1048576;
  static readonly EXTENSIONS = {
    video: ['m4v', 'avi', 'mpg', 'mp4', 'webm'],
    image: ['jpg', 'gif', 'bmp', 'png', 'tiff', 'jpeg', 'webp'],
  };

  /**
   * Magic byte signatures for MIME type validation.
   * Each MIME type maps to an array of alternative signatures (OR logic).
   * Each alternative is an array of byte-sequence parts that must ALL match (AND logic).
   */
  static readonly MAGIC_BYTES_SIGNATURES: Record<
    string,
    Array<Array<{ bytes: number[]; offset: number }>>
  > = {
    'image/jpeg': [[{ bytes: [0xff, 0xd8, 0xff], offset: 0 }]],
    'image/png': [
      [{ bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], offset: 0 }],
    ],
    'image/gif': [
      [{ bytes: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], offset: 0 }], // GIF87a
      [{ bytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], offset: 0 }], // GIF89a
    ],
    'image/bmp': [[{ bytes: [0x42, 0x4d], offset: 0 }]],
    'image/tiff': [
      [{ bytes: [0x49, 0x49, 0x2a, 0x00], offset: 0 }], // Little-endian TIFF
      [{ bytes: [0x4d, 0x4d, 0x00, 0x2a], offset: 0 }], // Big-endian TIFF
    ],
    'image/webp': [
      [
        { bytes: [0x52, 0x49, 0x46, 0x46], offset: 0 }, // RIFF header
        { bytes: [0x57, 0x45, 0x42, 0x50], offset: 8 }, // WEBP marker
      ],
    ],
  };

  static isVideo(fileName: string) {
    const extension = FilesUtils.getExtension(fileName);
    return FilesUtils.EXTENSIONS.video.includes(extension);
  }

  static isImage(fileName: string) {
    const extension = FilesUtils.getExtension(fileName);
    return FilesUtils.EXTENSIONS.image.includes(extension);
  }

  /**
   * Validates file content by reading its magic bytes (file signature).
   * This guards against files with spoofed extensions or Content-Type headers
   * by verifying the actual binary content matches an expected image format.
   */
  static async validateMagicBytes(
    file: File,
    allowedMimeTypes: string[]
  ): Promise<boolean> {
    const HEADER_SIZE = 12; // enough bytes to cover all supported signatures
    const buffer = await file.slice(0, HEADER_SIZE).arrayBuffer();
    const fileBytes = new Uint8Array(buffer);

    return allowedMimeTypes.some((mimeType) => {
      const signatures = FilesUtils.MAGIC_BYTES_SIGNATURES[mimeType];
      if (!signatures) return false;

      return signatures.some((parts) =>
        parts.every(({ bytes, offset }) =>
          bytes.every((byte, i) => fileBytes[offset + i] === byte)
        )
      );
    });
  }

  /**
   * Sanitizes a filename by stripping characters that could enable
   * path traversal or injection attacks, while preserving the extension.
   */
  static sanitizeFileName(fileName: string): string {
    const ext = FilesUtils.getExtension(fileName);
    const baseName = FilesUtils.getName(fileName);
    const sanitized = baseName.replace(/[^a-zA-Z0-9\-_]/g, '_');
    return ext ? `${sanitized}.${ext}` : sanitized;
  }

  static async uploadFile(file: File, url: string) {
    try {
      const { status, url: imageUrl } = await fetch(url, {
        body: file,
        method: 'PUT',
      });

      if (status !== 200) {
        throw new Error('Upload failed');
      }

      return imageUrl;
    } catch {
      throw new Error('Upload failed');
    }
  }

  static getFileFromURL$(url: string, name = 'newFile'): Promise<File> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        resolve(new File([xhr.response], name, { type: xhr.response.type }));
      };

      xhr.onerror = (e) => {
        xhr.abort();
        reject(e);
      };
      xhr.send();
    });
  }

  static getFileFromDropEntry$(entry: FileSystemFileEntry): Promise<File> {
    return new Promise((resolve, reject) => {
      entry.file(resolve, reject);
    });
  }

  /**
   * @param {number}  size - size of the file in MB
   */
  static getFileRestrictions({
    documentTypes,
    size = 10,
  }: {
    documentTypes: UploadDocumentType[];
    size?: number;
  }) {
    const fileFormat = {
      [UploadDocumentType.CSV]: ['.csv'],
      [UploadDocumentType.OFFICE_DOCUMENT_SPREADSHEET]: ['.xlsx'],
      [UploadDocumentType.MS_EXCEL]: ['.xls'],
    };

    const fileExtension = {
      [UploadDocumentType.CSV]: 'CSV',
      [UploadDocumentType.OFFICE_DOCUMENT_SPREADSHEET]: 'XLSX',
      [UploadDocumentType.MS_EXCEL]: 'XLS',
    };

    const types = documentTypes.reduce((acc, type) => {
      return {
        ...acc,
        [type]: fileFormat[type],
      };
    }, {} as { [k: string]: string[] });

    const supportedExtensions = documentTypes.map((type) => fileExtension[type]);

    return {
      types,
      supportedExtensions,
      size: size * FilesUtils.MEGABYTE,
    };
  }

  static getExtension(fileName: string) {
    return fileName.toLocaleLowerCase().split('.').pop() || '';
  }

  static getName(fileName: string) {
    return fileName.substring(0, fileName.lastIndexOf('.'));
  }

  static formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) {
      return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  static readFile$(file: File) {
    return new Promise<FileContent>((release, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        release(e.target?.result);
      };

      reader.onerror = () => {
        reject(new Error(`FileReader failed on file ${file.name}.`));
      };

      if (!file) {
        reject(new Error(`No file to read.`));
        return;
      }

      reader.readAsDataURL(file);
    });
  }

  static getFileType(fileName: string) {
    return FilesUtils.isImage(fileName)
      ? FileTypes.IMAGE
      : FilesUtils.isVideo(fileName)
      ? FileTypes.VIDEO
      : FileTypes.FILE;
  }

  static isExtension(fileName: string, extensions: Array<string>) {
    extensions = extensions.map((f) => f.replace('.', ''));
    const extension = FilesUtils.getExtension(fileName);

    return extensions.includes(extension);
  }

  static download(blob: Blob, name: string) {
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = name;
    a.click();
  }

  static async getImageSize(file: File) {
    return new Promise<{ width: number; height: number }>((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({
          height: img.height,
          width: img.width,
        });
      img.src = window.URL.createObjectURL(file);
    });
  }

  static open(filepath: string, inNewTab = true) {
    if (!filepath) {
      return;
    }

    const link = document.createElement('a');
    link.href = filepath;

    if (inNewTab) {
      link.target = '_blank';
      link.rel = 'noreferrer';
    }

    link.click();
    link.remove();
  }
}
